import { createContext, useContext, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { queryClient } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { useListNotifications, getListNotificationsQueryKey } from '@/shared/api/generated/notifications';
import { getGetAppointmentsQueryKey } from '@/shared/api/generated/appointments';
import { useNotificationStream } from '../api/useNotificationStream';
import type { Notification } from '../model/Notification';

const POLLING_INTERVAL_MS = 30_000;

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

type Props = {
  children: ReactNode;
};

export const NotificationsProvider = ({ children }: Props) => {
  const { isAuthenticated } = useAuth();

  // SSE stream — pushes new notifications into React Query cache
  const handleNotification = useCallback((notification: Notification) => {
    queryClient.setQueryData<Notification[]>(getListNotificationsQueryKey(), (prev) => {
      if (!prev) {
        return [notification];
      }
      // Deduplicate by ID (in case of reconnection overlap)
      if (prev.some((n) => n.id === notification.id)) {
        return prev;
      }
      return [notification, ...prev];
    });

    // A new public booking — invalidate the appointments list so the calendar shows it immediately
    if (notification.type === 'appointment_booked') {
      void queryClient.invalidateQueries({ queryKey: getGetAppointmentsQueryKey() });
    }
  }, []);

  const { isFallback } = useNotificationStream({
    enabled: isAuthenticated,
    onNotification: handleNotification,
  });

  // Initial fetch + fallback polling when SSE is unavailable
  const { data, isLoading: loading } = useListNotifications({
    query: {
      enabled: isAuthenticated,
      refetchInterval: isFallback ? POLLING_INTERVAL_MS : false,
    },
  });

  const notifications = useMemo<Notification[]>(() => (data as Notification[] | undefined) ?? [], [data]);

  // Polling-fallback path: detect new appointment_booked notifications that didn't come through SSE
  // and invalidate the appointments cache so the calendar refreshes.
  const seenIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (loading) {
      return;
    }
    if (seenIdsRef.current === null) {
      seenIdsRef.current = new Set(notifications.map((n) => n.id));
      return;
    }
    const seen = seenIdsRef.current;
    const hasNewBooking = notifications.some((n) => n.type === 'appointment_booked' && !seen.has(n.id));
    for (const n of notifications) {
      seen.add(n.id);
    }
    if (hasNewBooking) {
      void queryClient.invalidateQueries({ queryKey: getGetAppointmentsQueryKey() });
    }
  }, [notifications, loading]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.isRead).length, [notifications]);

  const value: NotificationsContextType = useMemo(
    () => ({ notifications, unreadCount, loading }),
    [notifications, unreadCount, loading]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
